// =============================================================================
// Contextual Help Content — hardcoded help text for common confusion points
//
// Each key maps to a short, plain-English explanation shown in an info popover.
// Keep text concise (2-3 sentences max) and action-oriented.
// =============================================================================

export const HELP_CONTENT: Record<string, { title: string; body: string }> = {
  // ── B2C Plans ──────────────────────────────────────────────────────────────
  'stripe-sync': {
    title: 'Stripe Sync Required',
    body: 'This plan needs to be synced to Stripe before users can check out. Click the plan card, then click "Sync to Stripe" in the detail panel.',
  },
  'stripe-toggle': {
    title: 'Stripe Payments Toggle',
    body: 'When ON, users see "Upgrade Now" on paid plans and can purchase via Stripe checkout. When OFF, they see "Coming Soon" instead.',
  },
  'coming-soon': {
    title: 'Coming Soon Badge',
    body: 'This shows because Stripe Payments is turned off above. Users cannot purchase plans until you enable Stripe Payments and sync each plan.',
  },
  'stripe-connect': {
    title: 'Stripe Connect',
    body: 'Stripe Connect lets your agency receive payments directly. Complete the onboarding process to link your bank account and start accepting money from users.',
  },
  'b2c-ceiling': {
    title: 'Plan Limit Ceiling',
    body: 'B2C plans cannot offer more than what your platform (B2B) plan allows. If a limit appears locked, your agency plan caps it at that level.',
  },
  'unravel-share': {
    title: 'Platform Commission',
    body: 'This percentage is the commission UnRavel takes from each subscription payment. The rest goes directly to your Stripe Connect account.',
  },
  'plan-count-limit': {
    title: 'Plan Count Limit',
    body: 'Your platform plan limits the total number of B2C plans you can create. Upgrade your agency plan to add more.',
  },

  // ── Billing ────────────────────────────────────────────────────────────────
  'prorated-charge': {
    title: 'Prorated Billing',
    body: 'You\'ll only be charged for the remaining days in your current billing period, not the full month. This adjusts automatically on your next invoice.',
  },
  'prorated-credit': {
    title: 'Prorated Credit',
    body: 'When removing seats, you receive a credit for the unused portion of the billing period. This is applied automatically to your next invoice.',
  },
  'plan-cancellation': {
    title: 'What Happens When You Cancel',
    body: 'Your plan stays active until the end of your current billing period. After that, you\'ll move to the Free plan. No data is lost — you can re-upgrade anytime.',
  },
  'invoice-open': {
    title: 'Open Invoice',
    body: 'This invoice has been issued but payment hasn\'t been collected yet. It will be charged to your payment method automatically.',
  },
  'invoice-uncollectible': {
    title: 'Uncollectible Invoice',
    body: 'Payment attempts for this invoice have failed. Please update your payment method to resolve this.',
  },
  'annual-savings': {
    title: 'Annual Billing Discount',
    body: 'Choosing annual billing saves ~20% compared to paying monthly. You\'re billed once per year instead of every month.',
  },
  'enterprise-contact': {
    title: 'Enterprise Plans',
    body: 'Enterprise plans require a custom setup. Clicking this will open an email to our sales team who will create a tailored plan for your needs.',
  },

  // ── Features ───────────────────────────────────────────────────────────────
  'locked-feature': {
    title: 'Locked Feature',
    body: 'This feature isn\'t included in your current plan. Upgrade to a higher plan to unlock it.',
  },
  'feature-limit': {
    title: 'Feature Limit',
    body: 'This number is the maximum allowed by your plan. When users reach this limit, the feature is restricted until the next period.',
  },
  'unlimited-symbol': {
    title: 'Unlimited',
    body: 'The "∞" symbol means there\'s no cap on this resource — use as much as you need.',
  },

  // ── Dashboard ──────────────────────────────────────────────────────────────
  'usage-window': {
    title: 'Usage Window',
    body: 'Usage metrics show the last 14 days of activity. Plan limits (like trips per month) are measured over a 30-day rolling window.',
  },
  'resource-bar': {
    title: 'Resource Usage',
    body: 'The bar shows how much of your plan limit is used. Amber means you\'re above 80% — consider upgrading soon.',
  },
};
